import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { finApi } from '../../api/finance.js';
import { hrApi } from '../../api/hr.js';
import { useAuth } from '../../contexts/AuthContext.jsx';

export default function Budgets() {
  const { can } = useAuth();
  const { id } = useParams();

  if (id) return <BudgetDetail id={id} canEdit={can('budget', 'update')} />;
  return <BudgetList canCreate={can('budget', 'create')} />;
}

function BudgetList({ canCreate }) {
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const nav = useNavigate();

  function refresh() { finApi.budgets().then((d) => setItems(d?.items || [])); }
  useEffect(() => {
    refresh();
    hrApi.listDepartments().then((d) => setDepartments(d?.items || [])).catch(() => {});
  }, []);

  async function submit(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const created = await finApi.createBudget({
      name: fd.get('name'),
      periodYear: Number(fd.get('periodYear')),
      type: fd.get('type'),
      departmentId: fd.get('departmentId') || null,
    });
    nav(`/finance/budgets/${created.budget.id}`);
  }

  return (
    <section>
      <header className="page-header">
        <h1>Budgets</h1>
        {canCreate && <button className="btn primary" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Close' : '+ New Budget'}</button>}
      </header>

      {showForm && (
        <form onSubmit={submit} className="card form-card grid-form">
          <label>Name<input name="name" required /></label>
          <label>Year<input name="periodYear" type="number" defaultValue={new Date().getFullYear()} required /></label>
          <label>
            Type
            <select name="type" defaultValue="department">
              <option value="department">Department</option>
              <option value="project">Project</option>
              <option value="company">Company</option>
            </select>
          </label>
          <label>
            Department
            <select name="departmentId" defaultValue="">
              <option value="">—</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </label>
          <div className="full actions"><button className="btn primary" type="submit">Create</button></div>
        </form>
      )}

      <div className="card">
        <table className="data-table">
          <thead><tr><th>Name</th><th>Year</th><th>Type</th><th>Department</th><th>Owner</th></tr></thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id}>
                <td><Link to={`/finance/budgets/${b.id}`}>{b.name}</Link></td>
                <td>{b.period_year}</td>
                <td><span className="chip">{b.type}</span></td>
                <td>{b.department_name || '—'}</td>
                <td className="muted">{b.owner_name || '—'}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="muted">No budgets yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function BudgetDetail({ id, canEdit }) {
  const [budget, setBudget] = useState(null);
  const [lines, setLines] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [vsActual, setVsActual] = useState(null);
  const [tab, setTab] = useState('plan');
  const [error, setError] = useState(null);

  async function refresh() {
    const b = await finApi.getBudget(id);
    setBudget(b?.budget);
    setLines(b?.lines || []);
    if (tab === 'actual') {
      const va = await finApi.budgetVsActual(id);
      setVsActual(va);
    }
  }

  useEffect(() => {
    refresh();
    finApi.accounts({ active: true }).then((d) => setAccounts(d || []));
    // eslint-disable-next-line
  }, [id, tab]);

  async function saveCell(line, key, value) {
    if (!canEdit) return;
    const next = { ...line, [key]: value };
    try {
      await finApi.upsertBudgetLine(id, {
        accountId: line.account_id,
        q1: Number(next.q1 ?? 0),
        q2: Number(next.q2 ?? 0),
        q3: Number(next.q3 ?? 0),
        q4: Number(next.q4 ?? 0),
      });
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
  }

  async function addLine(accountId) {
    try {
      await finApi.upsertBudgetLine(id, { accountId, q1: 0, q2: 0, q3: 0, q4: 0 });
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
  }

  if (!budget) return <div className="muted">Loading…</div>;

  const usedAccountIds = new Set(lines.map((l) => l.account_id));
  const available = accounts.filter((a) => !usedAccountIds.has(a.id));

  return (
    <section>
      <header className="page-header">
        <div>
          <h1>{budget.name}</h1>
          <p className="muted">{budget.period_year} · {budget.type}</p>
        </div>
        <Link to="/finance/budgets" className="btn ghost">← All budgets</Link>
      </header>

      {error && <div className="alert error">{error}</div>}

      <div className="tabs">
        <button className={tab === 'plan' ? 'tab active' : 'tab'} onClick={() => setTab('plan')}>Plan</button>
        <button className={tab === 'actual' ? 'tab active' : 'tab'} onClick={() => setTab('actual')}>Vs Actual</button>
      </div>

      {tab === 'plan' && (
        <div className="card">
          <table className="data-table budget-grid">
            <thead><tr><th>Account</th><th>Q1</th><th>Q2</th><th>Q3</th><th>Q4</th><th>Annual</th></tr></thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.id}>
                  <td><strong>{l.account_code}</strong> {l.account_name} <span className="muted small">{l.category_name}</span></td>
                  {['q1', 'q2', 'q3', 'q4'].map((q) => (
                    <td key={q}>
                      <input
                        type="number" step="0.01" defaultValue={Number(l[q])}
                        disabled={!canEdit}
                        onBlur={(e) => Number(e.target.value) !== Number(l[q]) && saveCell(l, q, e.target.value)}
                      />
                    </td>
                  ))}
                  <td><strong>{Number(l.annual_total).toFixed(2)}</strong></td>
                </tr>
              ))}
              {lines.length === 0 && <tr><td colSpan={6} className="muted">No budget lines yet.</td></tr>}
            </tbody>
          </table>

          {canEdit && available.length > 0 && (
            <div className="add-line">
              <label>
                Add account
                <select onChange={(e) => e.target.value && addLine(e.target.value)} value="">
                  <option value="">Select account…</option>
                  {available.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                </select>
              </label>
            </div>
          )}
        </div>
      )}

      {tab === 'actual' && vsActual && (
        <div className="card">
          <h3>Budget vs Actual · YTD</h3>
          <p className="muted">Total budget {vsActual.totals.budget.toFixed(2)} · actual {vsActual.totals.actual.toFixed(2)} · variance {vsActual.totals.variance.toFixed(2)}</p>

          <BudgetVsActualBars rows={vsActual.rows} />

          <table className="data-table">
            <thead><tr><th>Account</th><th>Budget</th><th>Actual</th><th>Variance</th></tr></thead>
            <tbody>
              {vsActual.rows.map((r) => (
                <tr key={r.lineId}>
                  <td><strong>{r.accountCode}</strong> {r.accountName}</td>
                  <td>{r.budget.toFixed(2)}</td>
                  <td>{r.actual.toFixed(2)}</td>
                  <td className={r.variance < 0 ? 'danger' : ''}>{r.variance.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function BudgetVsActualBars({ rows }) {
  const max = Math.max(1, ...rows.flatMap((r) => [Math.abs(r.budget), Math.abs(r.actual)]));
  return (
    <div className="bvsa">
      {rows.map((r) => (
        <div key={r.lineId} className="bvsa-row">
          <div className="bvsa-label">{r.accountName}</div>
          <div className="bvsa-bars">
            <div className="bvsa-bar bg-budget" style={{ width: `${(Math.abs(r.budget) / max) * 100}%` }}>
              <span>{r.budget.toFixed(0)}</span>
            </div>
            <div className="bvsa-bar bg-actual" style={{ width: `${(Math.abs(r.actual) / max) * 100}%` }}>
              <span>{r.actual.toFixed(0)}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
